import { parseWithZod } from '@conform-to/zod'
import { InquirySource } from '@prisma/client'
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from '@remix-run/node'
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/react'
import { typedjson, useTypedLoaderData } from 'remix-typedjson'

import { planContactFormSchema, PlanContactPage } from '~/features'
import { CustomError } from '~/lib'
import { createPlanInquiry } from '~/models/inquiry.server'
import { findPlanById } from '~/models/plan.server'

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { planId } = params
  if (!planId) throw new Response('Bad Request', { status: 400 })

  const plan = await findPlanById(Number(planId))

  if (!plan) {
    throw new Response('Not Found', { status: 404, statusText: 'Not Found.' })
  }

  return typedjson({ plan })
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { planId, prefectureSlug } = params

  if (!planId || !prefectureSlug) {
    throw new Response('Bad Request', { status: 400 })
  }

  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: planContactFormSchema,
  })

  if (submission.status !== 'success') {
    return submission.reply()
  }

  try {
    const result = await createPlanInquiry({
      name: submission.value.name,
      organization: submission.value.organization,
      email: submission.value.email,
      phone: submission.value.phone,
      numberOfPeople: submission.value.numberOfPeople ?? '',
      desiredMoveInDate: submission.value.desiredMoveInDate ?? '',
      budget: submission.value.budget ?? '',
      content: submission.value.content ?? '',
      spaceId: Number(planId),
      inquirySource: InquirySource.PLAN_PAGE,
      prefectureSlug,
    })

    if (!result.success) {
      throw new CustomError(result.message, {
        statusCode: result.statusCode,
      })
    }

    return redirect('/contact-success')
  } catch (error: unknown) {
    console.error('error', error)
    Sentry.captureException(error, {
      extra: {
        route: `/prefectures/${prefectureSlug}/offices/.../plans/${planId}/contact`,
      },
    })

    if (error instanceof CustomError) {
      throw new Response(null, {
        status: error.statusCode,
      })
    }

    throw new Response(null, {
      status: 500,
    })
  }
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const planName = data?.plan.name || ''
  const titlePrefix = planName ? `${planName}へのお問い合わせ` : 'お問い合わせ'
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    { title: titlePrefix + ' | シェアオフィス コワーキングスペース検索サイト' },
  ]
}

export default function Index() {
  const { plan } = useTypedLoaderData<typeof loader>()

  return <PlanContactPage plan={plan} />
}
